import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  Building2,
  Briefcase,
  FolderKanban,
  TrendingUp,
  Activity as ActivityIcon,
  Package,
  Truck,
  AlertTriangle,
  Clock,
  Download,
  Eye,
  Send,
  FileUp,
  FilePlus,
  Tag,
  ArrowUpRight,
  Search,
  Sparkles,
  HardDrive,
  Database,
} from "lucide-react";
import { api } from "../lib/api";

const ACTION_LABELS = {
  project_create: { label: "Progetti creati", icon: FilePlus },
  project_submit: { label: "Progetti inviati", icon: Send },
  project_file_upload: { label: "File progetto caricati", icon: FileUp },
  project_file_download: { label: "File progetto scaricati", icon: Download },
  project_file_preview: { label: "File progetto visualizzati", icon: Eye },
  asset_download: { label: "Asset fornitore scaricati", icon: Download },
  asset_preview: { label: "Asset fornitore visualizzati", icon: Eye },
  outlet_product_view: { label: "Schede outlet visualizzate", icon: Eye },
  discount_request: { label: "Richieste sconto", icon: Tag },
};

const STATUS_LABEL = {
  bozza: "Bozza",
  inviato: "Inviato",
  in_valutazione: "In valutazione",
  valorizzato: "Valorizzato",
  chiuso: "Chiuso",
  abbandonato: "Abbandonato",
};
const STATUS_COLOR = {
  bozza: "bg-slate-100 text-slate-700 border-slate-200",
  inviato: "bg-blue-50 text-blue-800 border-blue-200",
  in_valutazione: "bg-amber-50 text-amber-800 border-amber-200",
  valorizzato: "bg-emerald-50 text-emerald-800 border-emerald-200",
  chiuso: "bg-ink/10 text-ink border-ink/20",
  abbandonato: "bg-rose-50 text-rose-700 border-rose-200",
};

function money(n) {
  return `€ ${Math.round(n || 0).toLocaleString("it-IT")}`;
}

function formatBytes(bytes) {
  const n = Number(bytes || 0);
  if (n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const value = n / Math.pow(1024, i);
  // 2 decimals for MB+, 1 for KB, 0 for bytes
  const digits = i >= 2 ? 2 : i === 1 ? 1 : 0;
  return `${value.toFixed(digits).replace(".", ",")} ${units[i]}`;
}

function StatCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{label}</div>
        {Icon && <Icon size={14} className={accent || "text-slate1"} />}
      </div>
      <div className="mt-2 font-display text-3xl text-ink leading-none">{value}</div>
      {sub && <div className="mt-2 text-xs text-slate1">{sub}</div>}
    </div>
  );
}

function MiniBar({ value, max, accent = "bg-gold" }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="h-1.5 bg-ink/5 rounded-full overflow-hidden w-full">
      <div className={`h-full ${accent}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function AdminStats() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [magicTop, setMagicTop] = useState(null);
  const [magicDays, setMagicDays] = useState(30);
  // Top 10 fornitori più cercati (solo nome del fornitore — vedi `magic-search-top-suppliers`)
  const [magicTopSuppliers, setMagicTopSuppliers] = useState(null);
  // AI usage stats (Brave quota + Gemini cost del mese corrente)
  const [aiUsage, setAiUsage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/stats/dashboard");
        setData(res.data);
      } catch {
        toast.error("Errore caricamento statistiche");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    // Carica usage AI ogni 30 secondi così il counter è sempre fresco
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/admin/ai-usage/stats");
        if (!cancelled) setAiUsage(r.data);
      } catch {
        // silente
      }
    };
    load();
    const intervalId = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/stats/magic-search-top?days=${magicDays}&limit=10`);
        if (!cancelled) setMagicTop(r.data);
      } catch {
        // silente: la sezione mostra "nessun dato" se fallisce
      }
    })();
    return () => { cancelled = true; };
  }, [magicDays]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/stats/magic-search-top-suppliers?days=${magicDays}&limit=10`);
        if (!cancelled) setMagicTopSuppliers(r.data);
      } catch {
        // silente
      }
    })();
    return () => { cancelled = true; };
  }, [magicDays]);

  // Backup DB — anteprima + download
  const [backupSummary, setBackupSummary] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupDownloading, setBackupDownloading] = useState(false);

  useEffect(() => {
    // Carica anteprima backup una volta all'apertura della pagina.
    let cancelled = false;
    (async () => {
      setBackupLoading(true);
      try {
        const r = await api.get("/admin/db-export/summary");
        if (!cancelled) setBackupSummary(r.data);
      } catch {
        // silente: la card mostra "non disponibile"
      } finally {
        if (!cancelled) setBackupLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const downloadBackup = async () => {
    setBackupDownloading(true);
    try {
      const r = await api.get("/admin/db-export", { responseType: "blob", timeout: 0 });
      const now = new Date();
      const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
      const filename = `archi-hub-backup-${stamp}.json`;
      const blob = new Blob([r.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Backup scaricato: ${filename}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Errore download backup");
    } finally {
      setBackupDownloading(false);
    }
  };

  if (loading || !data) {
    return <div className="bg-white border border-ink/10 rounded-sm p-6 text-sm text-slate1">Caricamento statistiche...</div>;
  }

  const u = data.users;
  const p = data.projects;
  const a = data.activity;
  const c = data.catalog;
  const al = data.alerts;

  const actionsEntries = Object.entries(a.actions_breakdown_30d || {});
  const maxAction = actionsEntries.reduce((m, [, n]) => (n > m ? n : m), 0);

  return (
    <div className="space-y-8" data-testid="admin-stats">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">Statistiche</div>
          <div className="font-display text-2xl text-ink">Performance piattaforma</div>
        </div>
        <div className="text-[11px] tracking-[0.15em] uppercase text-slate1">
          Aggiornato {new Date(data.generated_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}
        </div>
      </div>

      {/* Alerts */}
      {(al.pending_users > 0 || al.pending_discount_requests > 0 || al.inactive_users > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3" data-testid="stats-alerts">
          <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-amber-900">
            {al.pending_users > 0 && <span><b>{al.pending_users}</b> approvazioni in attesa</span>}
            {al.pending_discount_requests > 0 && <span><b>{al.pending_discount_requests}</b> richieste sconto da evadere</span>}
            {al.inactive_users > 0 && <span><b>{al.inactive_users}</b> utenti inattivi (&gt;60gg)</span>}
          </div>
        </div>
      )}

      {/* AI Usage — Brave quota + Gemini cost */}
      {aiUsage && (
        <div data-testid="ai-usage-card">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3 inline-flex items-center gap-2">
            Costi AI · {aiUsage.month_key}
            <span className="ml-2 text-[9px] tracking-normal normal-case text-slate1/70">(auto-reset a inizio mese)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Brave quota */}
            <div className="bg-white border border-ink/10 rounded-sm p-4" data-testid="ai-usage-brave">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1">Brave Search · ricerche immagini</div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-display text-ink">{aiUsage.brave.quota_remaining}</span>
                <span className="text-sm text-slate1">/ {aiUsage.brave.quota_total} residue</span>
              </div>
              <div className="mt-3 h-2 bg-cream2 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${aiUsage.brave.quota_pct_used > 80 ? "bg-rose-500" : aiUsage.brave.quota_pct_used > 50 ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, aiUsage.brave.quota_pct_used)}%` }}
                />
              </div>
              <div className="mt-2 text-[10px] text-slate1 flex justify-between">
                <span>{aiUsage.brave.calls} chiamate questo mese</span>
                <span>{aiUsage.brave.quota_pct_used}% usato</span>
              </div>
            </div>

            {/* Gemini cost */}
            <div className="bg-white border border-ink/10 rounded-sm p-4" data-testid="ai-usage-gemini">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1">Gemini Vision · ranking foto</div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-display text-ink">${aiUsage.gemini_vision.cost_estimate_usd.toFixed(3)}</span>
                <span className="text-sm text-slate1">stimati</span>
              </div>
              <div className="mt-3 text-[10px] text-slate1">
                {aiUsage.gemini_vision.calls} chiamate × ${aiUsage.gemini_vision.cost_per_call_usd.toFixed(4)}/call
              </div>
              <div className="mt-1 text-[10px] text-slate1/70">
                Costo approssimato — addebitato sul saldo Universal Key Emergent
              </div>
            </div>

            {/* Top users */}
            <div className="bg-white border border-ink/10 rounded-sm p-4" data-testid="ai-usage-users">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-2">Top utenti per chiamate</div>
              {aiUsage.by_user.length === 0 ? (
                <div className="text-sm text-slate1/70 italic">Nessuna chiamata questo mese</div>
              ) : (
                <ul className="space-y-1.5">
                  {aiUsage.by_user.slice(0, 5).map((u) => (
                    <li key={u.email} className="flex justify-between text-xs">
                      <span className="text-ink truncate mr-2">{u.email}</span>
                      <span className="text-slate1 shrink-0">{u.calls}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          {aiUsage.brave.quota_pct_used > 80 && (
            <div className="mt-3 bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-sm text-xs">
              ⚠️ Brave quota oltre l&apos;80% — considera di passare al piano a pagamento o ridurre le ricerche AI. Le ricerche oltre 2000/mese costeranno extra.
            </div>
          )}
        </div>
      )}

      {/* Backup DB — export completo JSON per disaster recovery */}
      <div data-testid="db-backup-card">
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3 inline-flex items-center gap-2">
          <Database size={12} /> Backup Database
        </div>
        <div className="bg-white border border-ink/10 rounded-sm p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="font-display text-lg text-ink mb-1">Esporta tutto in JSON</div>
              <div className="text-sm text-slate1 max-w-2xl">
                Scarica un dump completo dei metadati (utenti senza password, fornitori, asset, progetti, contatti, wallet, contatori, ecc.). I file binari
                (PDF, immagini) restano su Object Storage — nel backup ci sono solo i loro <span className="font-mono text-xs">storage_path</span>.
              </div>
              {backupSummary && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Collezioni</div>
                    <div className="font-display text-2xl text-ink">
                      {backupSummary.collections.filter(c => c.included).length}
                    </div>
                    <div className="text-[10px] text-slate1">
                      + {backupSummary.excluded_collections.length} escluse
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Documenti totali</div>
                    <div className="font-display text-2xl text-ink">
                      {backupSummary.total_docs_included.toLocaleString("it-IT")}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Peso stimato</div>
                    <div className="font-display text-2xl text-ink">
                      {backupSummary.total_size_mb_included} MB
                    </div>
                    <div className="text-[10px] text-slate1">JSON reale ~= 50-80%</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Password hash</div>
                    <div className="font-display text-2xl text-emerald-700">Nascosti</div>
                    <div className="text-[10px] text-slate1">redatti per sicurezza</div>
                  </div>
                </div>
              )}
              {backupLoading && (
                <div className="mt-4 text-xs text-slate1">Analisi in corso...</div>
              )}
            </div>
            <button
              onClick={downloadBackup}
              disabled={backupDownloading || backupLoading}
              className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-2.5 text-xs tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-50 shrink-0"
              data-testid="db-backup-download-btn"
              title="Scarica il file JSON del backup"
            >
              <Download size={14} />
              {backupDownloading ? "Preparazione..." : "Scarica backup"}
            </button>
          </div>
          {backupSummary && (
            <details className="mt-4 group">
              <summary className="text-[11px] tracking-[0.2em] uppercase text-slate1 cursor-pointer hover:text-ink">
                Dettaglio collezioni ({backupSummary.collections.length})
              </summary>
              <div className="mt-3 max-h-64 overflow-auto border border-ink/10 rounded-sm">
                <table className="w-full text-xs" data-testid="db-backup-detail-table">
                  <thead className="bg-cream2 text-[10px] tracking-[0.15em] uppercase text-slate1 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Collezione</th>
                      <th className="text-right p-2">Docs</th>
                      <th className="text-right p-2">Size</th>
                      <th className="text-center p-2">Nel backup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backupSummary.collections.map((c) => (
                      <tr key={c.name} className={`border-t border-ink/5 ${!c.included ? "text-slate1/60" : ""}`}>
                        <td className="p-2 font-mono">{c.name}</td>
                        <td className="p-2 text-right">{c.count.toLocaleString("it-IT")}</td>
                        <td className="p-2 text-right">{formatBytes(c.size_bytes)}</td>
                        <td className="p-2 text-center">
                          {c.included ? <span className="text-emerald-700">✓</span> : <span className="text-slate1">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[10px] text-slate1 italic">
                {backupSummary.note}
              </div>
            </details>
          )}
        </div>
      </div>

      {/* Hero KPIs */}
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3 inline-flex items-center gap-2"><TrendingUp size={12} /> Volume d&apos;affari</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard label="Volume chiuso totale" value={money(p.closed_total)} accent="text-emerald-700" icon={TrendingUp} testid="stats-closed-total" />
          <StatCard label="Volume chiuso 30gg" value={money(p.closed_30d_value)} sub={`${p.closed_30d_count} progetti`} accent="text-emerald-700" icon={ArrowUpRight} testid="stats-closed-30d" />
          <StatCard label="In pipeline" value={money(p.pending_total)} sub="valutazione / approvato" accent="text-amber-700" icon={Clock} testid="stats-pipeline" />
          <StatCard label="Conversion rate" value={`${p.conversion_rate}%`} sub="progetti chiusi / inviati" icon={ActivityIcon} testid="stats-conversion" />
        </div>
      </div>

      {/* Users */}
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3 inline-flex items-center gap-2"><Users size={12} /> Utenti</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <StatCard label="Utenti totali" value={u.total} sub={`${u.approved} approvati · ${u.pending} in attesa`} icon={Users} testid="stats-users-total" />
          <StatCard label="Studi" value={u.studios} icon={Building2} accent="text-gold" testid="stats-studios" />
          <StatCard label="Trader" value={u.traders} icon={Briefcase} accent="text-blue-700" testid="stats-traders" />
          <StatCard label="Real Estate" value={u.real_estates || 0} icon={Users} accent="text-emerald-700" testid="stats-real-estates" />
          <StatCard label="Nuovi 30gg" value={u.new_30d} sub={`${u.active_30d} attivi · ${u.inactive} inattivi`} icon={ArrowUpRight} testid="stats-users-new" />
        </div>
      </div>

      {/* Real Estate Program */}
      {data.real_estate && (
        <div data-testid="stats-real-estate-section">
          <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-700 mb-3 inline-flex items-center gap-2">
            <Users size={12} /> Programma Real Estate
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              label="Contatti totali"
              value={data.real_estate.contacts_total}
              sub={`${data.real_estate.contacts_new_30d} nuovi 30gg`}
              icon={Users}
              testid="stats-re-contacts-total"
            />
            <StatCard
              label="Contatti confermati"
              value={data.real_estate.contacts_confirmed}
              sub={`${money(data.real_estate.credit_paid)} accreditati`}
              accent="text-emerald-700"
              icon={ArrowUpRight}
              testid="stats-re-contacts-confirmed"
            />
            <StatCard
              label="Contatti pending"
              value={data.real_estate.contacts_pending}
              sub={`${money(data.real_estate.credit_pending)} potenziali`}
              accent="text-amber-700"
              icon={Clock}
              testid="stats-re-contacts-pending"
            />
            <StatCard
              label="Contatti rifiutati"
              value={data.real_estate.contacts_rejected}
              sub={`€${data.real_estate.credit_per_contact} per contatto`}
              icon={AlertTriangle}
              testid="stats-re-contacts-rejected"
            />
          </div>
        </div>
      )}

      {/* Projects breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="bg-white border border-ink/10 rounded-sm p-5 xl:col-span-2" data-testid="stats-projects">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-4 inline-flex items-center gap-2"><FolderKanban size={12} /> Progetti per stato</div>
          <div className="space-y-3">
            {Object.entries(p.by_status).length === 0 && <div className="text-sm text-slate1">Nessun progetto.</div>}
            {Object.entries(p.by_status)
              .sort(([, a], [, b]) => b - a)
              .map(([status, n]) => {
                const total = p.total || 1;
                const pct = Math.round((n / total) * 100);
                return (
                  <div key={status} className="flex items-center gap-3">
                    <div className={`px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase rounded-sm border ${STATUS_COLOR[status] || ""}`}>
                      {STATUS_LABEL[status] || status}
                    </div>
                    <div className="flex-1"><MiniBar value={n} max={p.total} accent="bg-ink" /></div>
                    <div className="w-12 text-right text-sm text-ink font-medium">{n}</div>
                    <div className="w-12 text-right text-[10px] tracking-[0.15em] uppercase text-slate1">{pct}%</div>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="stats-by-account">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-4">Volume chiuso · per canale</div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between text-sm">
                <span className="inline-flex items-center gap-1.5 text-gold"><Building2 size={12} /> Studi</span>
                <span className="font-display text-lg text-ink">{money(p.closed_by_account?.studio || 0)}</span>
              </div>
              <div className="mt-1.5"><MiniBar value={p.closed_by_account?.studio || 0} max={p.closed_total || 1} accent="bg-gold" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm">
                <span className="inline-flex items-center gap-1.5 text-blue-700"><Briefcase size={12} /> Trader</span>
                <span className="font-display text-lg text-ink">{money(p.closed_by_account?.trader || 0)}</span>
              </div>
              <div className="mt-1.5"><MiniBar value={p.closed_by_account?.trader || 0} max={p.closed_total || 1} accent="bg-blue-600" /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Top performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="stats-top-studios">
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-3 inline-flex items-center gap-2">
            <Building2 size={12} /> Top studi
          </div>
          {data.top_studios.length === 0 ? (
            <div className="text-sm text-slate1 italic">Nessuno studio con progetti chiusi.</div>
          ) : (
            <ul className="space-y-2">
              {data.top_studios.map((s, i) => (
                <li key={s.id} className="flex items-center gap-3 py-1.5 border-b border-ink/5 last:border-b-0">
                  <span className="w-5 font-display text-slate1 text-sm text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate">{s.studio || s.name}</div>
                    <div className="text-[11px] text-slate1 truncate">{s.email} · {s.projects} progetti</div>
                  </div>
                  <div className="font-display text-base text-ink whitespace-nowrap">{money(s.closed_total)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="stats-top-traders">
          <div className="text-[11px] tracking-[0.2em] uppercase text-blue-700 mb-3 inline-flex items-center gap-2">
            <Briefcase size={12} /> Top trader
          </div>
          {data.top_traders.length === 0 ? (
            <div className="text-sm text-slate1 italic">Nessun trader con progetti chiusi.</div>
          ) : (
            <ul className="space-y-2">
              {data.top_traders.map((t, i) => (
                <li key={t.id} className="flex items-center gap-3 py-1.5 border-b border-ink/5 last:border-b-0">
                  <span className="w-5 font-display text-slate1 text-sm text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate">{t.studio || t.name}</div>
                    <div className="text-[11px] text-slate1 truncate">{t.email} · {t.projects} progetti</div>
                  </div>
                  <div className="font-display text-base text-ink whitespace-nowrap">{money(t.closed_total)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-ink/10 rounded-sm p-5 lg:col-span-2" data-testid="stats-top-real-estates">
          <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-700 mb-3 inline-flex items-center gap-2">
            <Users size={12} /> Top Real Estate · contatti confermati
          </div>
          {(!data.top_real_estates || data.top_real_estates.length === 0) ? (
            <div className="text-sm text-slate1 italic">Nessun real estate con contatti confermati.</div>
          ) : (
            <ul className="space-y-2">
              {data.top_real_estates.map((r, i) => (
                <li key={r.id} className="flex items-center gap-3 py-1.5 border-b border-ink/5 last:border-b-0">
                  <span className="w-5 font-display text-slate1 text-sm text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate">{r.agency || r.name}</div>
                    <div className="text-[11px] text-slate1 truncate">{r.email} · {r.confirmed_contacts} contatti confermati</div>
                  </div>
                  <div className="font-display text-base text-emerald-700 whitespace-nowrap">{money(r.credit_earned)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Catalog */}
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3 inline-flex items-center gap-2"><Package size={12} /> Catalogo &amp; fornitori</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Prodotti" value={c.products} sub={`${c.flash_products} in flash`} icon={Package} testid="stats-products" />
          <StatCard label="Fornitori" value={c.suppliers} icon={Truck} testid="stats-suppliers" />
          <StatCard label="Asset fornitori" value={c.supplier_assets} sub="cataloghi, listini, ecc." icon={Package} testid="stats-assets" />
          <StatCard label="Progetti totali" value={p.total} sub={`${p.new_30d} nuovi 30gg`} icon={FolderKanban} testid="stats-projects-total" />
        </div>
      </div>

      {/* Storage usage */}
      {data.storage && (
        <div data-testid="stats-storage-section">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3 inline-flex items-center gap-2">
            <HardDrive size={12} /> Spazio utilizzato
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div className="bg-white border border-ink/10 rounded-sm p-5 xl:col-span-1" data-testid="stats-storage-total">
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Totale piattaforma</div>
                <HardDrive size={14} className="text-gold" />
              </div>
              <div className="mt-2 font-display text-3xl text-ink leading-none">{formatBytes(data.storage.total_bytes)}</div>
              <div className="mt-2 text-xs text-slate1">Su Emergent Object Storage</div>
            </div>

            <div className="bg-white border border-ink/10 rounded-sm p-5 xl:col-span-2" data-testid="stats-storage-breakdown">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-4">Dettaglio per tipologia</div>
              <div className="space-y-3">
                {[
                  { key: "supplier_assets_bytes", label: "Asset fornitori (cataloghi PDF)", accent: "bg-gold" },
                  { key: "project_files_bytes", label: "File progetti", accent: "bg-ink" },
                  { key: "product_images_bytes", label: "Foto prodotti", accent: "bg-blue-600" },
                  { key: "product_files_bytes", label: "Schede tecniche prodotti", accent: "bg-emerald-600" },
                  { key: "banner_images_bytes", label: "Immagini banner", accent: "bg-slate-500" },
                ].map((row) => {
                  const val = Number(data.storage[row.key] || 0);
                  const total = Number(data.storage.total_bytes || 0);
                  const pct = total > 0 ? Math.round((val / total) * 100) : 0;
                  return (
                    <div key={row.key} className="flex items-center gap-3" data-testid={`stats-storage-row-${row.key}`}>
                      <div className="flex-1 text-sm text-ink truncate">{row.label}</div>
                      <div className="w-32"><MiniBar value={val} max={total || 1} accent={row.accent} /></div>
                      <div className="w-24 text-right text-sm text-ink font-medium whitespace-nowrap">{formatBytes(val)}</div>
                      <div className="w-12 text-right text-[10px] tracking-[0.15em] uppercase text-slate1">{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-ink/10 rounded-sm p-5 lg:col-span-2" data-testid="stats-activity-breakdown">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-4 inline-flex items-center gap-2">
            <ActivityIcon size={12} /> Attività · ultimi 30 giorni
          </div>
          {actionsEntries.length === 0 ? (
            <div className="text-sm text-slate1 italic">Nessuna attività registrata.</div>
          ) : (
            <div className="space-y-3">
              {actionsEntries.map(([action, n]) => {
                const meta = ACTION_LABELS[action] || { label: action, icon: ActivityIcon };
                const Icon = meta.icon;
                return (
                  <div key={action} className="flex items-center gap-3">
                    <div className="w-7 text-slate1"><Icon size={14} /></div>
                    <div className="flex-1 text-sm text-ink truncate">{meta.label}</div>
                    <div className="w-40"><MiniBar value={n} max={maxAction} accent="bg-ink" /></div>
                    <div className="w-12 text-right text-sm text-ink font-medium">{n}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="stats-top-assets">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3 inline-flex items-center gap-2">
            <Download size={12} /> Asset più scaricati · 90gg
          </div>
          {a.top_downloaded_assets_90d.length === 0 ? (
            <div className="text-sm text-slate1 italic">Nessun download recente.</div>
          ) : (
            <ul className="space-y-2.5">
              {a.top_downloaded_assets_90d.map((it, i) => (
                <li key={`${it.id}-${i}`} className="flex items-start gap-3">
                  <span className="w-5 text-slate1 text-sm text-right pt-0.5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate">{it.title || "—"}</div>
                    <div className="text-[11px] text-slate1 truncate">{it.supplier}</div>
                  </div>
                  <div className="text-xs tracking-[0.15em] uppercase text-slate1 whitespace-nowrap pt-0.5">{it.downloads} dl</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Top outlet product views · 30gg */}
      <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="stats-top-outlet-views">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-2">
            <Eye size={12} className="text-gold" /> Schede outlet più visualizzate · 30gg
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">
            {a.outlet_views_30d || 0} visualizzazioni totali
          </div>
        </div>
        {!Array.isArray(a.top_outlet_views_30d) || a.top_outlet_views_30d.length === 0 ? (
          <div className="text-sm text-slate1 italic">Nessuna visualizzazione di dettaglio prodotto nelle ultime 4 settimane.</div>
        ) : (
          <ul className="space-y-2.5">
            {a.top_outlet_views_30d.map((it, i) => (
              <li key={`${it.product_id}-${i}`} className="flex items-start gap-3" data-testid={`outlet-view-row-${i}`}>
                <span className="w-5 text-slate1 text-sm text-right pt-0.5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink truncate">
                    {it.product_brand ? <span className="text-slate1 mr-1">{it.product_brand} ·</span> : null}
                    {it.product_name || "—"}
                  </div>
                  <div className="text-[11px] text-slate1">
                    {it.unique_users} {it.unique_users === 1 ? "utente" : "utenti"} unic{it.unique_users === 1 ? "o" : "i"}
                    {it.last_at && ` · ultima il ${new Date(it.last_at).toLocaleDateString("it-IT")}`}
                  </div>
                </div>
                <div className="text-xs tracking-[0.15em] uppercase text-gold whitespace-nowrap pt-0.5 font-medium">
                  {it.views} {it.views === 1 ? "view" : "views"}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Magic Search · Top termini ricercati */}
      <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="stats-magic-search-top">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-2">
            <Sparkles size={12} className="text-gold" />
            Magic Search · Termini più cercati · {magicDays}gg
          </div>
          <div className="inline-flex border border-ink/10 rounded-sm overflow-hidden">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setMagicDays(d)}
                className={`px-3 py-1 text-[10px] tracking-[0.2em] uppercase transition-colors ${
                  magicDays === d
                    ? "bg-gold text-cream"
                    : "bg-white text-slate1 hover:bg-ink/5"
                }`}
                data-testid={`magic-top-range-${d}`}
              >
                {d}gg
              </button>
            ))}
          </div>
        </div>
        {!magicTop ? (
          <div className="text-sm text-slate1 italic">Caricamento…</div>
        ) : magicTop.items.length === 0 ? (
          <div className="text-sm text-slate1 italic">
            Nessuna ricerca registrata negli ultimi {magicDays} giorni.
          </div>
        ) : (
          <ul className="space-y-2.5">
            {magicTop.items.map((it, i) => {
              const maxCount = magicTop.items[0]?.count || 1;
              const clickable = Boolean(it.top_supplier_id);
              const goSearch = () => {
                if (!clickable) {
                  toast.info("Nessun fornitore associato a questo termine");
                  return;
                }
                navigate(`/fornitori?supplier=${encodeURIComponent(it.top_supplier_id)}&magic=${encodeURIComponent(it.term)}`);
              };
              return (
                <li
                  key={`${it.term}-${i}`}
                  className={`flex items-start gap-3 -mx-2 px-2 py-1 rounded-sm transition-colors ${clickable ? "cursor-pointer hover:bg-gold/10" : "opacity-90"}`}
                  data-testid={`magic-top-row-${i}`}
                  onClick={goSearch}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : -1}
                  onKeyDown={(e) => {
                    if (!clickable) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      goSearch();
                    }
                  }}
                  title={clickable ? `Cerca "${it.term}" nei listini di ${it.top_supplier}` : ""}
                >
                  <span className="w-6 text-slate1 text-sm text-right pt-0.5 font-medium">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Search size={11} className={clickable ? "text-gold shrink-0" : "text-slate1 shrink-0"} />
                      <span className={`text-sm font-medium truncate ${clickable ? "text-ink group-hover:text-gold" : "text-ink"}`}>&ldquo;{it.term}&rdquo;</span>
                      {clickable && (
                        <ArrowUpRight size={12} className="text-gold shrink-0" aria-hidden />
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-slate1">
                      {it.top_supplier && (
                        <span className="inline-flex items-center gap-1">
                          <Building2 size={10} /> {it.top_supplier}
                        </span>
                      )}
                      <span>{it.unique_users} utent{it.unique_users === 1 ? "e" : "i"}</span>
                      {it.last_at && (
                        <span title={new Date(it.last_at).toLocaleString("it-IT")}>
                          ultima: {new Date(it.last_at).toLocaleDateString("it-IT")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-32 pt-1">
                    <MiniBar value={it.count} max={maxCount} accent="bg-gold" />
                  </div>
                  <div className="w-14 text-right text-sm text-ink font-medium whitespace-nowrap pt-0.5">
                    {it.count} <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">ric.</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-3 text-[10px] tracking-[0.15em] uppercase text-slate1/70">
          I termini sono normalizzati (lowercase + trim). Multi-parola conta come una singola query.
          <span className="ml-2 text-gold">Clicca un termine per saltare alla Magic Search del fornitore top.</span>
        </div>
      </div>

      {/* Magic Search · Top 10 fornitori più cercati (solo nome fornitore) */}
      <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="stats-magic-search-top-suppliers">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-2">
            <Building2 size={12} className="text-gold" />
            Magic Search · Top 10 fornitori cercati · {magicDays}gg
          </div>
          <div className="text-[10px] tracking-[0.15em] uppercase text-slate1/70">
            Stesso periodo del riquadro sopra
          </div>
        </div>
        {!magicTopSuppliers ? (
          <div className="text-sm text-slate1 italic">Caricamento…</div>
        ) : magicTopSuppliers.items.length === 0 ? (
          <div className="text-sm text-slate1 italic">
            Nessuna ricerca per fornitore registrata negli ultimi {magicDays} giorni.
          </div>
        ) : (
          <ul className="space-y-2.5">
            {magicTopSuppliers.items.map((it, i) => {
              const maxCount = magicTopSuppliers.items[0]?.count || 1;
              const clickable = Boolean(it.supplier_id);
              const goSupplier = () => {
                if (!clickable) return;
                navigate(`/fornitori?supplier=${encodeURIComponent(it.supplier_id)}`);
              };
              return (
                <li
                  key={`${it.supplier_id || it.supplier_name}-${i}`}
                  className={`flex items-start gap-3 -mx-2 px-2 py-1 rounded-sm transition-colors ${clickable ? "cursor-pointer hover:bg-gold/10" : "opacity-90"}`}
                  data-testid={`magic-top-supplier-row-${i}`}
                  onClick={goSupplier}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : -1}
                  onKeyDown={(e) => {
                    if (!clickable) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      goSupplier();
                    }
                  }}
                  title={clickable ? `Apri scheda ${it.supplier_name}` : ""}
                >
                  <span className="w-6 text-slate1 text-sm text-right pt-0.5 font-medium">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Building2 size={11} className={clickable ? "text-gold shrink-0" : "text-slate1 shrink-0"} />
                      <span className="text-sm font-medium truncate text-ink">{it.supplier_name}</span>
                      {clickable && <ArrowUpRight size={12} className="text-gold shrink-0" aria-hidden />}
                    </div>
                    <div className="mt-1 text-[11px] text-slate1">
                      <span>{it.unique_users} utent{it.unique_users === 1 ? "e" : "i"}</span>
                      {it.last_at && (
                        <span className="ml-3" title={new Date(it.last_at).toLocaleString("it-IT")}>
                          ultima: {new Date(it.last_at).toLocaleDateString("it-IT")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-32 pt-1">
                    <MiniBar value={it.count} max={maxCount} accent="bg-gold" />
                  </div>
                  <div className="w-14 text-right text-sm text-ink font-medium whitespace-nowrap pt-0.5">
                    {it.count} <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">ric.</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-3 text-[10px] tracking-[0.15em] uppercase text-slate1/70">
          Conteggio per fornitore (ogni occorrenza di ricerca pesa 1). Esclude le ricerche degli admin.
        </div>
      </div>

      <div className="text-[10px] tracking-[0.15em] uppercase text-slate1 text-center pt-4">
        Attività totale ultimi 7gg: {a.last_7d} · ultimi 30gg: {a.last_30d}
      </div>
    </div>
  );
}
